import React, { useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { Grid, Typography, useTheme } from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import PriorityHighOutlinedIcon from "@mui/icons-material/PriorityHighOutlined";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box";
import { token } from "@atlaskit/tokens";
import invariant from "tiny-invariant";
import { getItemData, isItemData } from "../../../utils/dndUtils.js";
import { useListContext } from "./ThirdPlaceList.js";

const idleState = { type: "idle" };
const draggingState = { type: "dragging" };

export const ThirdPlaceListItem = (props) => {
  //item = team
  const { key, item, index, updateThirdPlacePicks, thirdPlaceTeams } = props;
  const theme = useTheme();
  const { registerItem, instanceId } = useListContext();

  const ref = useRef(null);
  const dragHandleRef = useRef(null);

  const [draggableState, setDraggableState] = useState(idleState);
  const [closestEdge, setClosestEdge] = useState(null);

  const listItemId = `third-pick-${item?.id}`;

  const getListItemStyles = (position) => {
    if (item?.flaggedForEdit) {
      return {
        backgroundColor: theme.branding.orange[50],
        borderColor: theme.branding.orange[500],
      };
    } else {
      if (position <= 7) {
        return {
          backgroundColor: theme.branding.green[50],
          borderColor: theme.branding.green[500],
        };
      } else if (position <= 11) {
        return {
          backgroundColor: theme.branding.yellow[100],
          borderColor: theme.branding.yellow[500],
        };
      }
    }
  };

  useEffect(() => {
    const element = ref?.current;
    const dragHandle = dragHandleRef?.current;
    invariant(element);
    invariant(dragHandle);

    const data = getItemData({ item, index, instanceId });

    const onChange = ({ source, self }) => {
      const isSource = source.element === dragHandle;
      if (isSource) {
        setClosestEdge(null);
        return;
      }

      const closestEdge = extractClosestEdge(self.data);

      const sourceIndex = source.data.index;
      invariant(typeof sourceIndex === "number");

      const isItemBeforeSource = index === sourceIndex - 1;
      const isItemAfterSource = index === sourceIndex + 1;

      const isDropIndicatorHidden =
        (isItemBeforeSource && closestEdge === "bottom") ||
        (isItemAfterSource && closestEdge === "top");

      if (isDropIndicatorHidden) {
        setClosestEdge(null);
        return;
      }

      setClosestEdge(closestEdge);
    };

    return combine(
      registerItem({ itemId: listItemId, element }),
      draggable({
        element: dragHandle,
        getInitialData: () => data,
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({
              x: token("space.200"),
              y: token("space.100"),
            }),
            render({ container }) {
              setDraggableState({ type: "preview", container });

              return () => setDraggableState(draggingState);
            },
          });
        },
        onDragStart() {
          setDraggableState(draggingState);
        },
        onDrop() {
          setDraggableState(idleState);
          //EDIT: Add PUT user picks here
        },
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          return (
            isItemData(source.data) && source.data.instanceId === instanceId
          );
        },
        getData({ input }) {
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ["top", "bottom"],
          });
        },
        onDragEnter: onChange,
        onDrag: onChange,
        onDragLeave() {
          setClosestEdge(null);
        },
        onDrop({ location }) {
          setClosestEdge(null);

          if (location?.initial === location?.current) {
            //do nothing if position didn't change
            return;
          } else {
            const newIndex = location?.current?.dropTargets[0]?.data?.index;
            const currIndex = location?.initial?.dropTargets[0]?.data?.index;

            updateThirdPlacePicks(newIndex, currIndex, item, thirdPlaceTeams);
          }
        },
      }),
    );
  }, [
    instanceId,
    item,
    index,
    registerItem,
    dragHandleRef,
    ref,
    listItemId,
    updateThirdPlacePicks,
    thirdPlaceTeams,
  ]);

  return (
    <Fragment key={key} sx={{ width: "100%" }}>
      <Grid container ref={ref} sx={{ position: "relative", width: "100%" }}>
        <Grid
          ref={dragHandleRef}
          direction="row"
          wrap="nowrap"
          sx={[
            {
              width: "100%",
              position: "relative",
              padding: "12px",
              alignItems: "center",
              overflow: "hidden",
              cursor: "grab",
              display: "flex",
              borderRadius: "10px",
              borderLeft: item?.flaggedForEdit ? "auto" : "4px solid",
              border: item?.flaggedForEdit ? "2px solid" : "auto",
              justifyContent: "space-between",
            },
            getListItemStyles(index),
            // We are applying the disabled effect to the inner element so that the border and drop indicator are not affected.
            draggableState.type === "dragging" && { opacity: 0.4 },
          ]}
        >
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{
              alignItems: "center",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              gap: "12px",
              width: "100%",
            }}
          >
            <Grid
              container
              sx={{
                height: "24px",
                maxWidth: "24px",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "100%",
                backgroundColor: item?.flaggedForEdit
                  ? theme.branding.orange[500]
                  : index < 8
                    ? theme.branding.green[500]
                    : theme.branding.yellow[500],
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.branding.gray[0], fontSize: "12px" }}
              >
                {item?.flaggedForEdit ? (
                  <PriorityHighOutlinedIcon />
                ) : (
                  index + 1
                )}
              </Typography>
            </Grid>
            <Grid
              container
              sx={{
                width: "32px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={item?.imageUrl}
                alt={`${item?.name}-country-flag-icon`}
                style={{ maxWidth: "32px", maxHeight: "18px" }}
              />
            </Grid>
            <Grid container direction="column">
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={{
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Typography variant="subtitle1">{item.name}</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.branding.gray[400], fontWeight: 500 }}
                >
                  #{item.worldRank}
                </Typography>
              </Grid>
              <Typography variant="caption">Group {item.group}</Typography>
            </Grid>
          </Grid>
          <DragIndicatorOutlinedIcon
            sx={{ height: "16px", color: theme.branding.gray[400] }}
          />
        </Grid>
        {closestEdge && (
          <DropIndicator edge={closestEdge} gap="8px" type="no-terminal" />
        )}
      </Grid>
      {draggableState.type === "preview" &&
        createPortal(
          <Grid
            direction="row"
            wrap="nowrap"
            sx={[
              {
                position: "relative",
                padding: "12px",
                alignItems: "center",
                overflow: "hidden",
                cursor: "grab",
                display: "flex",
                borderRadius: "10px",
                borderLeft: "4px solid",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                gap: "12px",
              },
              getListItemStyles(index),
              // We are applying the disabled effect to the inner element so that the border and drop indicator are not affected.
              draggableState.type === "dragging" && { opacity: 0.4 },
            ]}
          >
            <Grid
              container
              sx={{
                height: "24px",
                maxWidth: "24px",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "100%",
                backgroundColor: item?.flaggedForEdit
                  ? theme.branding.orange[500]
                  : index < 8
                    ? theme.branding.green[500]
                    : theme.branding.yellow[500],
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.branding.gray[0], fontSize: "12px" }}
              >
                {item?.flaggedForEdit ? (
                  <PriorityHighOutlinedIcon />
                ) : (
                  index + 1
                )}
              </Typography>
            </Grid>
            <Grid
              container
              sx={{
                width: "32px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={item?.imageUrl}
                alt={`${item?.name}-country-flag-icon`}
                style={{ maxWidth: "32px", maxHeight: "18px" }}
              />
            </Grid>
            <Grid
              container
              direction="column"
              sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis" }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                {item?.name}
              </Typography>
              <Typography variant="caption">Group {item?.group}</Typography>
            </Grid>
          </Grid>,
          draggableState.container,
        )}
    </Fragment>
  );
};

export default ThirdPlaceListItem;
